import React, { useState } from 'react';

const formatTime = (dateStr) => {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const CalendarMonthView = ({ currentDate, meetings, onDateClick, onEventClick, onEventHover }) => {
  const [popover, setPopover] = useState(null); // { date, events, x, y }

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

  const isSameDay = (d1, d2) => 
    d1?.getDate() === d2?.getDate() && 
    d1?.getMonth() === d2?.getMonth() && 
    d1?.getFullYear() === d2?.getFullYear();

  const handleDayClick = (date, e) => {
    if (!date) return;
    onDateClick(date);
  };

  const handleMoreClick = (date, dayMeetings, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({ 
      date, 
      events: dayMeetings, 
      x: rect.left, 
      top: rect.top,
      bottom: rect.bottom
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider border-r last:border-r-0 border-slate-200">
                {day}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 auto-rows-[minmax(120px,_1fr)]">
            {days.map((date, i) => {
              const dayMeetings = date ? meetings.filter(m => isSameDay(new Date(m.meetingStart), date)) : [];
              const isToday = isSameDay(date, new Date());
              
              return (
                <div 
                  key={i} 
                  onClick={(e) => handleDayClick(date, e)}
                  className={`border-r border-b border-slate-100 p-1 transition-colors relative group
                    ${!date ? 'bg-slate-50/50' : 'bg-white hover:bg-slate-50 cursor-pointer'}
                    ${i % 7 === 6 ? 'border-r-0' : ''}
                  `}
                >
                  {date && (
                    <div className="flex flex-col h-full">
                      <div className={`text-xs font-medium w-7 h-7 flex items-center justify-center rounded-full mb-1 shrink-0
                        ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 group-hover:text-blue-600'}
                      `}>
                        {date.getDate()}
                      </div>
                      
                      <div className="flex flex-col gap-1 flex-1 overflow-hidden">
                        {dayMeetings.slice(0, 2).map((m) => (
                          <div 
                            key={m.id} 
                            onClick={(e) => { e.stopPropagation(); onEventClick(m, e); }}
                            className="bg-blue-100/60 hover:bg-blue-100 border-l-[3px] border-blue-600 text-blue-900 px-1.5 py-1 rounded-r-md rounded-l-sm text-[10px] leading-tight cursor-pointer truncate transition-colors"
                          >
                            <span className="opacity-80 font-medium mr-1">{formatTime(m.meetingStart)}</span>
                            <span className="font-bold">{m.title}</span>
                          </div>
                        ))}
                        {dayMeetings.length > 2 && (
                          <div 
                            onClick={(e) => handleMoreClick(date, dayMeetings, e)}
                            className="text-[10px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-1.5 py-0.5 rounded cursor-pointer mt-auto self-start transition-colors"
                          >
                            +{dayMeetings.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {popover && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPopover(null)} />
          <div 
            className="fixed z-50 bg-white rounded-lg shadow-xl border border-slate-200 p-3 w-64 max-h-80 overflow-y-auto"
            style={{ 
              ...(popover.bottom > window.innerHeight / 2 
                ? { bottom: `${window.innerHeight - popover.top + 5}px` } 
                : { top: `${popover.bottom + 5}px` }),
              left: Math.min(popover.x, window.innerWidth - 270) 
            }}
          >
            <h4 className="text-sm font-bold text-slate-800 mb-3 pb-2 border-b border-slate-100">
              {popover.date.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h4>
            <div className="flex flex-col gap-2">
              {popover.events.map(m => (
                <div 
                  key={m.id} 
                  onClick={(e) => { setPopover(null); onEventClick(m, e); }}
                  className="bg-blue-100/60 hover:bg-blue-100 border-l-[3px] border-blue-600 text-blue-900 px-2 py-1.5 rounded-r-md rounded-l-sm cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="opacity-80 font-medium text-[10px] whitespace-nowrap">{formatTime(m.meetingStart)}</span>
                    <h3 className="text-xs font-bold truncate">{m.title}</h3>
                  </div>
                  <div className="text-[10px] text-blue-700/80">
                    {formatTime(m.meetingStart)} - {formatTime(m.meetingEnd)} {m.location ? `• ${m.location}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarMonthView;
